import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import './filter_sort.scss';
import { Checkbox, Button } from 'antd';
import { useDispatch } from 'react-redux';
import { getAllProfiles } from '../../../slices/ads/thunk';
import dropdownArrow from '../../../assets/images/dropdown_arrow_dark.svg'


const FilterSort = ({ featureStatus, filterCatch, filterDropdownStatus }) => {
    const dispatch = useDispatch();
    const [selectedValueFilter, setSelectedValueFilter] = useState({
        key: 'all',
        value: 'All Profiles',
    });
    const [selectedValueSort, setSelectedValueSort] = useState({
        key: '',
        value: '',
    });
    const [filterDropdownVisible, setFilterDropdownVisible] = useState(false)
    const [sortDropdownVisibleState, setSortDropdownVisibleState] = useState(false)
    const [filterSelected, setFilterSelected] = useState('All Profiles');
    const [sortSelected, setSortSelected] = useState('Sort');
    const limit = 6;
    const debounceTimeoutRef = useRef(null);
    const isApiCallInProgress = useRef(false);
    const filterDropdownRef = useRef(null);
    const sortDropdownRef = useRef(null);
    // Updated status list with new options
    const statusList = useMemo(() => ({
        all: { filterType: 'all', value: 'All Profiles', key: 'all' },
        approved: { filterType: 'status', value: 'Approved', key: 'approved' },
        pending: { filterType: 'status', value: 'In review', key: 'pending' },
        rejected: { filterType: 'status', value: 'Rejected', key: 'rejected' },
        disabled: { filterType: 'status', value: 'Paused', key: 'disabled' },
    }), []);

    // Sort options
    const sortOptions = useMemo(() => ({
        newest: { sortType: 'created_at', value: 'Newest First', key: 'newest', apiValue: -1 },
        oldest: { sortType: 'created_at', value: 'Oldest First', key: 'oldest', apiValue: 1 },
        // nameAsc: { sortType: 'name', value: 'Name A-Z', key: 'nameAsc' },
        // nameDesc: { sortType: 'name', value: 'Name Z-A', key: 'nameDesc' },
    }), []);

    const handleChangeFilter = (key, filterType) => {
        setSelectedValueFilter({ key, value: statusList[key]?.value || '' });
        setFilterSelected(statusList[key]?.value || 'Filters');
        setFilterDropdownVisible(false);
        
        // Call the parent component's filter function
        if (filterCatch) {
            filterCatch(filterType, key);
        }
        
        // Debounce the API call with current sort value
        debouncedFetchProfiles(key, selectedValueSort.key);
    };

    const handleChangeSort = (key, sortType) => {
        setSelectedValueSort({ key, value: sortOptions[key]?.value || '' });
        setSortSelected(sortOptions[key]?.value || 'Sort');
        setSortDropdownVisibleState(false);
        
        // Debounce the API call with current filter value
        debouncedFetchProfiles(selectedValueFilter.key, key);
    };

    const fetchProfiles = useCallback(async (status = 'all', sort = '') => {
        // Prevent multiple concurrent API calls
        if (isApiCallInProgress.current) {
            return;
        }
        
        try {
            isApiCallInProgress.current = true;
            
            // Don't pass status parameter when 'all' is selected
            const statusParam = status === 'all' ? null : status;
            const sortOption = sort ? sortOptions[sort]?.apiValue : null;
            
            await dispatch(getAllProfiles({
                page: 1,
                limit,
                status: statusParam,
                filters: null,
                created_at: sortOption,
                forceRefresh: true
            }));
        } catch (error) {
            console.error('Error fetching profiles:', error);
        } finally {
            isApiCallInProgress.current = false;
        }
    }, [dispatch, limit, sortOptions]);

    // Debounced version of fetchProfiles
    const debouncedFetchProfiles = useCallback((status, sort) => {
        // Prevent multiple concurrent API calls
        if (isApiCallInProgress.current) {
            return;
        }
        
        // Clear existing timeout
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
        
        // Set new timeout
        debounceTimeoutRef.current = setTimeout(() => {
            if (!isApiCallInProgress.current) {
                fetchProfiles(status, sort);
            }
        }, 300); // 300ms debounce delay
    }, [fetchProfiles]);

    useEffect(() => {
        setFilterDropdownVisible(false)
    }, [filterDropdownStatus])

    useEffect(() => {
        // Initialize with all profiles (no status filter) only once
        const initializeProfiles = async () => {
            if (isApiCallInProgress.current) return;
            
            try {
                isApiCallInProgress.current = true;
                await dispatch(getAllProfiles({
                    page: 1,
                    limit,
                    status: null, // No status filter for all profiles
                    filters: null,
                    created_at: null,
                    forceRefresh: true
                }));
            } catch (error) {
                console.error('Error fetching profiles:', error);
            } finally {
                isApiCallInProgress.current = false;
            }
        };
        
        initializeProfiles();
    }, [dispatch, limit]); // Only depend on dispatch and limit

    // Cleanup effect to clear debounce timeout on unmount
    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    // Click outside handler for both dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Close filter dropdown if click is outside
            if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
                setFilterDropdownVisible(false);
            }
            
            // Close sort dropdown if click is outside
            if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
                setSortDropdownVisibleState(false);
            }
        };

        // Add event listener when either dropdown is open
        if (filterDropdownVisible || sortDropdownVisibleState) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        // Cleanup
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [filterDropdownVisible, sortDropdownVisibleState]);

    return (
        <div className="filter-sort-container">
            <ul className="parent_listing">
                {/* Filter Dropdown */}
                <li ref={filterDropdownRef}>
                        <div
                            className="filter_sort_cnt md"
                            onClick={() => {
                                setFilterDropdownVisible((prev) => !prev);
                                // Close sort dropdown when filter dropdown is opened
                                if (!filterDropdownVisible) {
                                    setSortDropdownVisibleState(false);
                                }
                            }}
                        >
                            <Button type='primary'>
                                {filterSelected} <span className='icon dropdown_arrow'><img src={dropdownArrow} alt="" /></span>
                            </Button>
                        </div>

                        <div className="filter_sort_dropdown">
                            <div className={`dropdown added_closeBtn outer ${filterDropdownVisible && 'active'}`}>
                                <ul style={{ paddingLeft: '0' }} >
                                    {Object.entries(statusList).map(([key, status]) => (
                                        <li key={status.key} className={`${filterSelected === status.value ? 'active' : ''} sm`}>
                                            <Checkbox
                                                className='md'
                                                id={status.key}
                                                value={status.key}
                                                checked={selectedValueFilter.value === status.value}
                                                onChange={() => {
                                                    handleChangeFilter(key, status.filterType);
                                                }}
                                            >
                                                {`${status.value}`}
                                            </Checkbox>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </li>

                    {/* Sort Dropdown */}
                    <li ref={sortDropdownRef}>
                        <div
                            className="filter_sort_cnt md"
                            onClick={() => {
                                setSortDropdownVisibleState((prev) => !prev);
                                // Close filter dropdown when sort dropdown is opened
                                if (!sortDropdownVisibleState) {
                                    setFilterDropdownVisible(false);
                                }
                            }}
                        >
                            <Button type='primary'>
                                {sortSelected} <span className='icon dropdown_arrow'><img src={dropdownArrow} alt="" /></span>
                            </Button>
                        </div>

                        <div className="filter_sort_dropdown">
                            <div className={`dropdown added_closeBtn outer ${sortDropdownVisibleState && 'active'}`}>
                                <ul style={{ paddingLeft: '0' }} >
                                    {Object.entries(sortOptions).map(([key, sort]) => (
                                        <li key={sort.key} className={`${sortSelected === sort.value ? 'active' : ''} sm`}>
                                            <Checkbox
                                                className='md'
                                                id={sort.key}
                                                value={sort.key}
                                                checked={selectedValueSort.value === sort.value}
                                                onChange={() => {
                                                    handleChangeSort(key, sort.sortType);
                                                }}
                                            >
                                                {`${sort.value}`}
                                            </Checkbox>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </li>
                </ul>
        </div>
    );
};

export default FilterSort;
